import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Tables } from '@/integrations/supabase/types';

export type Category = Tables<'categories'>;
export type Occasion = Tables<'occasions'>;
export type Product = Tables<'products'>;

export const useCategories = () => {
  return useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      return data;
    }
  });
};

/**
 * Occasions that actually have at least one available, non-addon product.
 * Mirror of useCategoriesWithProducts — use in nav menus so we never link
 * to an occasion that dead-ends on "No products found for this occasion."
 */
export const useOccasionsWithProducts = () => {
  return useQuery({
    queryKey: ['occasions-with-products'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('occasions')
        .select('*, products!inner(id)')
        .eq('is_active', true)
        .eq('products.is_available', true)
        .eq('products.is_addon', false)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      const seen = new Set<string>();
      const unique: any[] = [];
      for (const occ of data || []) {
        if (seen.has(occ.id)) continue;
        seen.add(occ.id);
        const { products, ...occasionOnly } = occ as any;
        unique.push(occasionOnly);
      }
      return unique;
    },
  });
};

/**
 * Categories that actually have at least one available, non-addon product.
 * Use this for navigation menus — hides empty categories that would
 * otherwise dead-end on "Category not found".
 */
export const useCategoriesWithProducts = () => {
  return useQuery({
    queryKey: ['categories-with-products'],
    queryFn: async () => {
      // Single query: categories ⨝ products, then we de-duplicate client-side.
      // PostgREST doesn't support GROUP BY directly, so we fetch categories
      // joined with at least one available product.
      const { data, error } = await supabase
        .from('categories')
        .select('*, products!inner(id)')
        .eq('is_active', true)
        .eq('products.is_available', true)
        .eq('products.is_addon', false)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      // De-dupe by category id (the !inner join multiplies rows by product count).
      const seen = new Set<string>();
      const unique: any[] = [];
      for (const cat of data || []) {
        if (seen.has(cat.id)) continue;
        seen.add(cat.id);
        // Drop the joined products array — caller only needs category metadata.
        const { products, ...categoryOnly } = cat as any;
        unique.push(categoryOnly);
      }
      return unique;
    },
  });
};

export const useOccasions = () => {
  return useQuery({
    queryKey: ['occasions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('occasions')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      
      if (error) throw error;
      return data;
    }
  });
};

interface UseProductsOptions {
  categoryId?: string;
  occasionId?: string;
  featured?: boolean;
  sortBy?: string;
  tags?: string[];
  vendorIds?: string[];
  includeIds?: string[];
  city?: string;
  /**
   * Whether to include addon items (balloons, cards, etc.) in the result set.
   * Default: false — addons are hidden from main listings and only surfaced
   * through the cart upsell strip. Set to true on screens that want them
   * explicitly (e.g. the cart's "Add to your order" component, admin views).
   */
  includeAddons?: boolean;
}

export const useProducts = (filters?: UseProductsOptions) => {
  return useQuery({
    queryKey: ['products', filters],
    queryFn: async () => {
      let query = supabase
        .from('products')
        .select(`
          *,
          categories (name_en, name_ar, slug),
          occasions (name_en, name_ar, slug),
          vendors!inner (
            id,
            name_en,
            name_ar,
            delivers_to_all_cities,
            vendor_delivery_locations (
              city
            )
          ),
          product_delivery_locations (city)
        `)
        .eq('is_available', true)
        .eq('vendors.is_active', true);

      // Hide addons from main listings unless caller explicitly opts in.
      if (!filters?.includeAddons) {
        query = query.eq('is_addon', false);
      }

      if (filters?.categoryId) {
        query = query.eq('category_id', filters.categoryId);
      }
      
      if (filters?.occasionId) {
        query = query.eq('occasion_id', filters.occasionId);
      }
      
      if (filters?.featured) {
        query = query.eq('is_featured', true);
      }

      if (filters?.tags && filters.tags.length > 0) {
        query = query.overlaps('tags', filters.tags);
      }

      if (filters?.vendorIds && filters.vendorIds.length > 0) {
        query = query.in('vendor_id', filters.vendorIds);
      }

      if (filters?.includeIds && filters.includeIds.length > 0) {
        query = query.in('id', filters.includeIds);
      }

      // Sorting
      switch (filters?.sortBy) {
        case 'price-asc':
          query = query.order('price', { ascending: true });
          break;
        case 'price-desc':
          query = query.order('price', { ascending: false });
          break;
        case 'name-asc':
          query = query.order('name_en', { ascending: true });
          break;
        case 'name-desc':
          query = query.order('name_en', { ascending: false });
          break;
        case 'oldest':
          query = query.order('created_at', { ascending: true });
          break;
        case 'newest':
        default:
          query = query.order('created_at', { ascending: false });
          break;
      }

      const { data, error } = await query;
      
      if (error) throw error;

      // Strict city filtering
      if (filters?.city) {
        return (data || []).filter(product =>
          product.product_delivery_locations?.some(
            (loc: any) => loc.city === filters.city
          )
        );
      }

      return data;
    }
  });
};

export const useProductBySlug = (slug: string) => {
  return useQuery({
    queryKey: ['product', slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          categories (name_en, name_ar, slug),
          occasions (name_en, name_ar, slug)
        `)
        .eq('slug', slug)
        .eq('is_available', true)
        .single();
      
      if (error) throw error;
      return data;
    }
  });
};

export const useCategoryBySlug = (slug: string) => {
  return useQuery({
    queryKey: ['category', slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('slug', slug)
        .eq('is_active', true)
        .single();
      
      if (error) throw error;
      return data;
    }
  });
};

export const useOccasionBySlug = (slug: string) => {
  return useQuery({
    queryKey: ['occasion', slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('occasions')
        .select('*')
        .eq('slug', slug)
        .eq('is_active', true)
        .single();
      
      if (error) throw error;
      return data;
    }
  });
};

/**
 * Cart upsell strip: every product with `is_addon = true`. These are the
 * small accessories (balloons, cards, candles) that should appear as
 * "Add to your order" inline in the cart, never as standalone products.
 */
export const useAddons = (city?: string) => {
  return useQuery({
    queryKey: ['addons', city],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          categories (name_en, name_ar, slug),
          vendors!inner (
            id,
            delivers_to_all_cities,
            vendor_delivery_locations (city)
          ),
          product_delivery_locations (city)
        `)
        .eq('is_addon', true)
        .eq('is_available', true)
        .eq('vendors.is_active', true)
        .order('price', { ascending: true });

      if (error) throw error;

      // Same city-availability rule as the main catalogue, so we never
      // suggest an addon the active vendor can't fulfill.
      if (city) {
        return (data || []).filter((p: any) =>
          p.product_delivery_locations?.some((loc: any) => loc.city === city)
          || p.vendors?.delivers_to_all_cities,
        );
      }
      return data || [];
    },
  });
};

export const useCitiesWithProducts = () => {
  return useQuery({
    queryKey: ['cities-with-products'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select(`
          vendors!inner (
            delivers_to_all_cities,
            vendor_delivery_locations (city)
          )
        `)
        .eq('is_available', true)
        .eq('is_addon', false)
        .eq('vendors.is_active', true);

      if (error) throw error;

      const cities = new Set<string>();

      data?.forEach(product => {
        const vendor = product.vendors;
        if (!vendor) return;

        // Only add specific delivery cities (ignore delivers_to_all_cities)
        vendor.vendor_delivery_locations?.forEach((location: any) => {
          cities.add(location.city);
        });
      });

      return Array.from(cities);
    }
  });
};
